namespace PortalLibs.Client.Components


open System
open FSharpPlus
open Browser.Types
open Fable.Core.JsInterop
open Fable.React
open Fable.React.Props
open PortalLibs.Client
open PortalLibs.Client.Util
open Fulma
open Fulma.Extensions.Wikiki
open Feliz
open PortalLibs.Client.Model
open PortalLibs.Shared


module NumberInput =
  let private parseInputOrZero =
    JsUtil.parseFloat >> fun x -> if Double.IsNaN x then 0.0 else x
    
  let private valueOr condition boundaryOpt value =
    match boundaryOpt with
    | None -> value
    | Some b -> if condition b value then b else value
  
  let private valueOrMin = valueOr (fun m v -> v < m)
  let private valueOrMax = valueOr (fun m v -> v > m)
    
  let NumberInput =
    React.memo (
      "NumberInput",
      areEqual = Helpers.equalsButFunctions,
      render =
        (fun (props: {| Key: string
                        Value: float option
                        Step: float option
                        Min: float option
                        Max: float option
                        DecimalPlaces: int option
                        Ref: IRefValue<HTMLElement option> option
                        EditMode: ControlEditMode<float> |}) ->
          DebouncedView.DebouncedView
            {| Key = $"NumberInputDebouncedView-%s{props.Key}"
               Value = props.Value
               ValueChanged =
                 match props.EditMode with
                 | ControlEditMode.ReadOnly -> (fun _ -> ())
                 | ControlEditMode.Edit edit -> edit.OnChange
               Intercept =
                 Some
                   (fun values ->
                     parseInputOrZero values.Value
                     |> valueOrMin props.Min
                     |> valueOrMax props.Max
                     |> Some)
               View =
                 (fun valueOpt valueChanged ->
                   Html.input [
                     prop.className "input"
                     prop.type'.number
                     match props.Ref with
                     | Some r -> prop.ref r
                     | None -> ()
                     prop.onClick (fun e -> (e.target :?> HTMLInputElement).select())
                     prop.onChange (fun (e: Browser.Types.Event) -> !!e.target?value |> valueChanged)
                     prop.value (
                       valueOpt
                       |>> fun floatVal ->
                            match props.DecimalPlaces with
                            | None -> string floatVal
                            | Some places -> floatVal.ToString $"F%i{places}"
                       |> Option.emptyStrIfNone )
                     prop.style [ style.textAlign.right ]
                     
                     match props.Step with
                     | None -> ()
                     | Some step -> prop.step step
                     match props.Min with
                     | None -> ()
                     | Some min -> prop.min min
                     match props.Max with
                     | None -> ()
                     | Some max -> prop.max max
                     
                     match props.EditMode with
                     | ControlEditMode.ReadOnly ->
                       prop.readOnly true
                       prop.tabIndex -1
                     | _ -> ()
                   ] ) |}),
      withKey = fun props -> props.Key)

  let viewWithHeader header props = field header [ NumberInput props ]

module Checkradio =
  importAll "bulma-checkradio/dist/css/bulma-checkradio.min.css"
  let YesNoAllRadio =
    React.memo (
      "YesNoAllRadio",
      areEqual = Helpers.equalsButFunctions,
      render =
        (fun (props: {| Key: string
                        Value: bool option
                        ValueChanged: bool option -> unit |}) ->
          Field.div [] [
            Checkradio.radioInline [ Checkradio.Name "inline"
                                     Checkradio.Id $"checkradio-%s{props.Key}-all"
                                     Checkradio.Checked(Option.isNone props.Value)
                                     Checkradio.OnChange(fun _ -> props.ValueChanged None) ] [
              str "All"
            ]
            Checkradio.radioInline [ Checkradio.Name "inline"
                                     Checkradio.Id $"checkradio-%s{props.Key}-yes"
                                     Checkradio.Checked(props.Value = Some true)
                                     Checkradio.OnChange(fun _ -> props.ValueChanged(Some true)) ] [
              str "Yes "
            ]
            Checkradio.radioInline [ Checkradio.Name "inline"
                                     Checkradio.Id $"checkradio-%s{props.Key}-no"
                                     Checkradio.Checked(props.Value = Some false)
                                     Checkradio.OnChange(fun _ -> props.ValueChanged(Some false)) ] [
              str "No "
            ]
          ]),
      withKey = fun props -> props.Key)

  let CheckboxComp =
    React.memo (
      "CheckboxComp",
      areEqual = Helpers.equalsButFunctions,
      render =
        (fun (props: {| Key: string
                        EditMode: ControlEditMode<bool>
                        Label: string option
                        Value: bool |}) ->
        div [ Style [ FlexShrink 0 ] ] [
          Checkradio.checkbox [ Checkradio.Id $"checkbox-%s{props.Key}"
                                Checkradio.Checked(props.Value)
                                Checkradio.InputProps [
                                  match props.EditMode with
                                  | ReadOnly -> ()
                                  | Edit edit ->
                                    OnKeyDown (ifEnter (fun _ -> props.Value |> not |> Some |> edit.OnChange))
                                  
                                  match props.EditMode with
                                  | Edit mode ->
                                    if mode.StopPropagation then
                                      OnClick(fun event -> event.stopPropagation ())
                                  | _ -> ()
                                  Style [
                                    match props.EditMode with
                                    | ReadOnly -> CSSProp.PointerEvents "none"
                                    | _ -> ()

                                    ]
                                ]
                                Checkradio.LabelProps [
                                  match props.EditMode with
                                  | Edit mode ->
                                    if mode.StopPropagation then
                                      OnClick(fun event -> event.stopPropagation ())
                                  | _ -> ()
                                  Style [
                                    match props.EditMode with
                                    | ReadOnly -> CSSProp.PointerEvents "none"
                                    | _ -> ()
                                    if props.Label.IsNone then
                                      PaddingLeft "0"
                                  ]
                                ]
                                match props.EditMode with
                                | ReadOnly -> Checkradio.Disabled true
                                | Edit edit ->
                                  Checkradio.OnChange(fun _ -> props.Value |> not |> Some |> edit.OnChange) ] [
            match props.Label with
            | None -> ()
            | Some label -> str label
          ]
        ]),
      withKey = fun props -> props.Key)
